/**
 * Сервис оффлайн-сохранения видео из раздела «Образование» (мобильные устройства).
 * Скачивание через fetch() по stream URL (с конвертацией чанков в base64 для Filesystem).
 * URL стрима на мобильных подменяется на API из конфига в admin.service.
 */
import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import AdminService from './admin.service';

const EDUCATION_OFFLINE_DIR = 'Education_Offline';
const SECTION_NAMES_FILENAME = 'section_names.json';
const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB — меньше чанки реже упираются в таймаут
const FETCH_TIMEOUT_MS = 90000; // 90 с на чанк
const MAX_CHUNK_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function getOfflineFileName(videoId: string): string {
  // edu:123 -> edu_123, google fileId stays as-is
  if (videoId.startsWith('edu:')) {
    return `edu_${videoId.slice(4)}`;
  }
  return videoId;
}

function getOfflineFilePath(sectionKey: string, videoId: string): string {
  const fileName = getOfflineFileName(videoId);
  return `${EDUCATION_OFFLINE_DIR}/${sectionKey}/${fileName}.mp4`;
}

/** Имя файла без .mp4 → id для списка (edu_123 → edu:123, иначе как есть). */
function fileNameToVideoId(fileName: string): string {
  if (fileName.startsWith('edu_')) {
    return `edu:${fileName.slice(4)}`;
  }
  return fileName;
}

export default class EducationOfflineService {
  private adminService = new AdminService();
  private cancelledDownloads = new Set<string>();

  isMobile(): boolean {
    return Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';
  }

  async getStreamUrl(videoId: string): Promise<string> {
    if (videoId.startsWith('edu:')) {
      const raw = videoId.slice(4).trim();
      const id = Number(raw);
      if (!Number.isInteger(id) || id < 1) throw new Error('Invalid education video id');
      return this.adminService.getEducationVideoStreamUrl(id);
    }
    return this.adminService.getGoogleVideoStreamUrl(videoId);
  }

  async isOffline(sectionKey: string, videoId: string): Promise<boolean> {
    if (!this.isMobile()) return false;
    try {
      const path = getOfflineFilePath(sectionKey, videoId);
      const stat = await Filesystem.stat({
        path,
        directory: Directory.Data,
      });
      return stat.size > 0;
    } catch {
      return false;
    }
  }

  async getOfflinePath(sectionKey: string, videoId: string): Promise<string | null> {
    if (!this.isMobile()) return null;
    const ok = await this.isOffline(sectionKey, videoId);
    if (!ok) return null;
    try {
      const path = getOfflineFilePath(sectionKey, videoId);
      const { uri } = await Filesystem.getUri({
        path,
        directory: Directory.Data,
      });
      return Capacitor.convertFileSrc(uri);
    } catch {
      return null;
    }
  }

  /**
   * Список ключей разделов, в которых есть офлайн-видео (для оффлайн-режима).
   */
  async getOfflineSectionKeys(): Promise<string[]> {
    if (!this.isMobile()) return [];
    try {
      const { files } = await Filesystem.readdir({
        path: EDUCATION_OFFLINE_DIR,
        directory: Directory.Data,
      });
      const keys: string[] = [];
      const fileList = Array.isArray(files) ? files : [];
      for (const file of fileList) {
        const name = typeof file === 'string' ? file : (file as { name?: string }).name;
        if (!name || typeof name !== 'string') continue;
        if (typeof file !== 'string' && (file as { type?: string }).type === 'directory') {
          keys.push(name);
        } else {
          try {
            const stat = await Filesystem.stat({
              path: `${EDUCATION_OFFLINE_DIR}/${name}`,
              directory: Directory.Data,
            });
            if (stat.type === 'directory') keys.push(name);
          } catch {
            // skip: нет доступа или не директория
          }
        }
      }
      return keys;
    } catch {
      return [];
    }
  }

  /**
   * Список офлайн-видео в разделе (по файлам в Education_Offline/sectionKey).
   * Возвращает { id, name } для отображения в списке в оффлайн-режиме.
   */
  async getOfflineVideosInSection(sectionKey: string): Promise<Array<{ id: string; name: string }>> {
    if (!this.isMobile()) return [];
    try {
      const path = `${EDUCATION_OFFLINE_DIR}/${sectionKey}`;
      const { files } = await Filesystem.readdir({
        path,
        directory: Directory.Data,
      });
      const items: Array<{ id: string; name: string }> = [];
      const fileList = Array.isArray(files) ? files : [];
      for (const file of fileList) {
        try {
          const name = typeof file === 'string' ? file : (file as { name?: string }).name;
          if (!name || typeof name !== 'string' || !name.endsWith('.mp4')) continue;
          const baseName = name.slice(0, -4);
          const id = fileNameToVideoId(baseName);
          items.push({ id, name: baseName.replace(/^edu_/, 'Видео ') });
        } catch {
          // пропускаем битую или неожиданную запись
        }
      }
      items.sort((a, b) => a.name.localeCompare(b.name, 'ru', { numeric: true }));
      return items;
    } catch {
      return [];
    }
  }

  /**
   * Сохранить маппинг folderKey → имя раздела (с сервера) для отображения в оффлайне.
   */
  async saveSectionNames(sections: Array<{ name: string; folderKey: string }>): Promise<void> {
    if (!this.isMobile() || !sections?.length) return;
    try {
      const map: Record<string, string> = {};
      for (const s of sections) {
        const key = s?.folderKey;
        const name = s?.name;
        if (key != null && String(key).trim() !== '' && name != null && typeof name === 'string') {
          map[String(key).trim()] = name;
        }
      }
      if (Object.keys(map).length === 0) return;
      try {
        await Filesystem.mkdir({
          path: EDUCATION_OFFLINE_DIR,
          directory: Directory.Data,
          recursive: true,
        });
      } catch (e: unknown) {
        const msg = (e as Error)?.message ?? String(e);
        if (!/directory exists|already exists|EEXIST/i.test(msg)) throw e;
      }
      await Filesystem.writeFile({
        path: `${EDUCATION_OFFLINE_DIR}/${SECTION_NAMES_FILENAME}`,
        data: JSON.stringify(map),
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
    } catch {
      // не критично для работы
    }
  }

  /**
   * Прочитать сохранённый маппинг folderKey → имя раздела (для оффлайн-режима).
   * При ошибке чтения, битом JSON или неверной структурой возвращает {}.
   */
  async getSectionNames(): Promise<Record<string, string>> {
    if (!this.isMobile()) return {};
    try {
      const { data } = await Filesystem.readFile({
        path: `${EDUCATION_OFFLINE_DIR}/${SECTION_NAMES_FILENAME}`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      const raw = typeof data === 'string' ? data : '';
      if (!raw.trim()) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      const result: Record<string, string> = {};
      for (const k of Object.keys(parsed)) {
        if (typeof k === 'string' && k && typeof parsed[k] === 'string') result[k] = parsed[k];
      }
      return result;
    } catch {
      return {};
    }
  }

  cancelDownload(videoId: string): void {
    this.cancelledDownloads.add(videoId);
  }

  private async fetchChunkWithRetry(
    streamUrl: string,
    rangeStart: number,
    rangeEnd: number,
    videoId: string
  ): Promise<Response> {
    let lastError: unknown;
    for (let attempt = 0; attempt < MAX_CHUNK_RETRIES; attempt++) {
      if (this.cancelledDownloads.has(videoId)) {
        throw new Error('Download cancelled');
      }
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      try {
        const res = await fetch(streamUrl, {
          headers: { Range: `bytes=${rangeStart}-${rangeEnd}` },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        return res;
      } catch (e) {
        clearTimeout(timeoutId);
        lastError = e;
        if (attempt < MAX_CHUNK_RETRIES - 1) {
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        }
      }
    }
    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  }

  async downloadVideo(
    videoId: string,
    sectionKey: string,
    onProgress?: (downloadedMB: number, totalMB: number) => void
  ): Promise<void> {
    if (!this.isMobile()) {
      throw new Error('Offline download is only available on mobile');
    }
    this.cancelledDownloads.delete(videoId);
    const streamUrl = await this.getStreamUrl(videoId);
    const dirPath = `${EDUCATION_OFFLINE_DIR}/${sectionKey}`;
    const fileName = `${getOfflineFileName(videoId)}.mp4`;
    const fullPath = `${dirPath}/${fileName}`;
    const tempPath = `${dirPath}/dwn_${getOfflineFileName(videoId)}.mp4`;

    try {
      await Filesystem.mkdir({
        path: dirPath,
        directory: Directory.Data,
        recursive: true,
      });
    } catch (e: unknown) {
      const msg = (e as Error)?.message ?? String(e);
      if (!/directory exists|already exists|EEXIST/i.test(msg)) {
        throw e;
      }
    }

    let startByte = 0;
    let totalBytes = 0;

    onProgress?.(0, 0);

    const appendChunk = async (data: ArrayBuffer) => {
      const base64 = arrayBufferToBase64(data);
      if (!base64) return;
      try {
        await Filesystem.stat({
          path: tempPath,
          directory: Directory.Data,
        });
        await Filesystem.appendFile({
          path: tempPath,
          data: base64,
          directory: Directory.Data,
        });
      } catch {
        await Filesystem.writeFile({
          path: tempPath,
          data: base64,
          directory: Directory.Data,
          recursive: true,
        });
      }
    };

    const firstRes = await this.fetchChunkWithRetry(streamUrl, 0, CHUNK_SIZE - 1, videoId);
    if (firstRes.status !== 206 && firstRes.status !== 200) {
      throw new Error(`Download failed: ${firstRes.status}`);
    }
    const contentRange = firstRes.headers.get('Content-Range');
    if (contentRange) {
      const match = contentRange.match(/bytes \d+-\d+\/(\d+)/);
      if (match) totalBytes = parseInt(match[1], 10);
    }
    if (!totalBytes) {
      const cl = firstRes.headers.get('Content-Length');
      if (cl) totalBytes = parseInt(cl, 10);
    }
    const totalMB = totalBytes > 0 ? totalBytes / (1024 * 1024) : 0;
    const firstData = await firstRes.arrayBuffer();
    await appendChunk(firstData);
    startByte = firstData.byteLength;
    onProgress?.(startByte / (1024 * 1024), totalMB);

    while (!this.cancelledDownloads.has(videoId)) {
      if (totalBytes > 0 && startByte >= totalBytes) break;
      const endByte = totalBytes > 0
        ? Math.min(startByte + CHUNK_SIZE - 1, totalBytes - 1)
        : startByte + CHUNK_SIZE - 1;
      const res = await this.fetchChunkWithRetry(streamUrl, startByte, endByte, videoId);
      if (res.status !== 206 && res.status !== 200) break;
      const chunk = await res.arrayBuffer();
      if (chunk.byteLength === 0) break;
      await appendChunk(chunk);
      startByte += chunk.byteLength;
      onProgress?.(startByte / (1024 * 1024), totalMB);
      if (chunk.byteLength < CHUNK_SIZE) break;
    }

    if (this.cancelledDownloads.has(videoId)) {
      this.cancelledDownloads.delete(videoId);
      try {
        await Filesystem.deleteFile({
          path: tempPath,
          directory: Directory.Data,
        });
      } catch {
        // ignore
      }
      throw new Error('Download cancelled');
    }

    await Filesystem.rename({
      from: tempPath,
      to: fullPath,
      directory: Directory.Data,
    });
  }

  async removeVideo(sectionKey: string, videoId: string): Promise<void> {
    if (!this.isMobile()) return;
    try {
      const path = getOfflineFilePath(sectionKey, videoId);
      await Filesystem.deleteFile({
        path,
        directory: Directory.Data,
      });
    } catch {
      // ignore
    }
  }
}
